// Global Instructions Rule Applied!
//
// InvestmentDetailOptionsPanel — small section rendered above the FilterPanel
// inside the Filtering drawer for the Fund Investment Detail grid. Surfaces
// the at__get_measures parameters that act as filters but are passed as RPC
// arguments rather than as filter rows.
//
// State is owned by the hook (session-only, never persisted).

import React from 'react'
import { Checkbox, Space } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs, { type Dayjs } from 'dayjs'
import type { FundInvestmentDetailOptions } from '../hooks/useFundInvestmentDetail.ts'

interface Props {
  value: FundInvestmentDetailOptions
  onChange: (next: FundInvestmentDetailOptions) => void
}

export function InvestmentDetailOptionsPanel({ value, onChange }: Props): React.ReactElement {
  const datetimeValue: Dayjs | null = value.datetime ? dayjs(value.datetime) : null

  const handleDateChange = (d: Dayjs | null) => {
    // Send end-of-day in the user's local TZ. The picker is date-only (no
    // time component), so "21 Aug" means "as of 23:59:59 on 21 Aug in my
    // timezone". d.toISOString() on a fresh dayjs is midnight-local — for
    // any TZ east of UTC that maps to the prior UTC day, and the backend's
    // `transaction_date <= p_datetime` filter then excludes valid same-day
    // rows. d.endOf('day') captures the user's intent unambiguously.
    onChange({ ...value, datetime: d ? d.endOf('day').toISOString() : null })
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>Options</div>
      <Space direction='vertical' size={6} className='w-full'>
        <Checkbox
          checked={value.includeWarehoused}
          onChange={(e) => onChange({ ...value, includeWarehoused: e.target.checked })}
        >
          Include warehoused
        </Checkbox>
        <Checkbox
          checked={value.includeConvertedOut}
          onChange={(e) => onChange({ ...value, includeConvertedOut: e.target.checked })}
        >
          Include converted out
        </Checkbox>
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-600 min-w-[64px]'>As of:</span>
          <DatePicker
            value={datetimeValue}
            onChange={handleDateChange}
            placeholder='Now'
            format='YYYY-MM-DD'
            allowClear
            className='flex-1'
          />
        </div>
      </Space>
    </div>
  )
}

export default InvestmentDetailOptionsPanel
